import React, { useCallback, useMemo } from 'react';
import { CustomTableCell } from '../../../components/customTable';
import { StyledTableRow } from './StyledTableRow';
import { Invoice } from '../../../redux/invoices';
import { Box, IconButton, Theme, Tooltip, Typography } from '@mui/material';
import { priceFormat } from '../../../components/tableCellRenders';
import { getDateDifference } from '../../../utils';
import { DateTime } from 'luxon';
import { SCREEN_WIDTH } from '../../../components/finKitAvatar/breakpoints';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { useAppDispatch } from '../../../redux/hooks';
import { notificationAlert } from '../../../redux/notifications';

export const InvoiceDataRow = ({ data }: { data: Invoice }) => {
  const dispatch = useAppDispatch();
  const columnWidth = (theme: Theme) => theme.spacing(10);

  const numberOfDueDays = useMemo(() => {
    if (!data.isPaidAfterPastDue || !data.paidOn) {
      return '—';
    }
    return getDateDifference(
      new Date(data.invoiceDueDate),
      new Date(data.paidOn),
    );
  }, [data]);

  const openInvoiceDocument = useCallback((fileId?: string) => {
    if (!fileId) return;
    window.open(`${fileId}`, '_blank', 'noopener,noreferrer');
  }, []);

  const isDueDateInPast = useMemo(() => {
    return !data.paidOn && new Date(data.invoiceDueDate) < new Date();
  }, [data.invoiceDueDate, data.paidOn]);

  const shortId = useMemo(() => {
    if (data.id.length <= 8) return data.id;
    return `${data.id.slice(0, 4)}...${data.id.slice(-4)}`;
  }, [data.id]);

  const handleCopyIdToClipboard = useCallback(
    (
      event:
        | React.MouseEvent<HTMLDivElement, MouseEvent>
        | React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      event?.stopPropagation();
      navigator.clipboard.writeText(data.id);
      dispatch(notificationAlert('Invoice id copied!'));
    },
    [data, dispatch],
  );

  return (
    <StyledTableRow isLastRow={false} key={data.id}>
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box>{data.projectName ? data.projectName : '—'}</Box>
      </CustomTableCell>
      <CustomTableCell
        sx={{
          borderBottom: '0',
          minWidth: columnWidth,
          py: 1,
          '&:hover': {
            color: (theme) =>
              data.invoiceUrl ? theme.palette.primary.main : '',
          },
          cursor: data.invoiceUrl ? 'pointer' : '',
        }}
        pure
        nowrap
        onClick={() => openInvoiceDocument(data?.invoiceUrl)}
      >
        <Box
          component="span"
          sx={{
            textDecoration: 'underline',
          }}
        >
          {shortId}
        </Box>
        <Box
          component="span"
          sx={{
            marginInlineStart: 1,
            color: 'primary.main',
          }}
        >
          ↗
        </Box>
        <Tooltip title="copy" placement="right-start">
          <IconButton
            onClick={handleCopyIdToClipboard}
            sx={{
              padding: 0,
              ':hover': {
                background: 'none',
                backgroundColor: 'transparent',
              },
            }}
          >
            <FileCopyOutlinedIcon
              sx={{
                fontSize: '0.8rem',
                color: 'primary.main',
                marginInlineStart: 1,
              }}
            />
          </IconButton>
        </Tooltip>
      </CustomTableCell>
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="end"
          justifyContent="end"
        >
          <Typography
            align={'right'}
            sx={{ fontWeight: '500', marginRight: '-20px' }}
          >
            {priceFormat({
              value: data.amount ? Math.round(data.amount) : data.amount,
            })}
          </Typography>
        </Box>
      </CustomTableCell>

      {/* Debt */}
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="end"
          justifyContent="end"
        >
          <Typography
            align={'right'}
            sx={{ fontWeight: '500', marginRight: '-20px' }}
          >
            {priceFormat({
              value: data.debt ? Math.round(data.debt) : data.debt,
            })}
          </Typography>
        </Box>
      </CustomTableCell>

      {/* Paid */}
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="end"
          justifyContent="end"
        >
          <Typography
            align={'right'}
            sx={{ fontWeight: '500', marginRight: '-20px' }}
          >
            {priceFormat({
              value: data.amountPaid
                ? Math.round(data.amountPaid)
                : data.amountPaid,
            })}
          </Typography>
        </Box>
      </CustomTableCell>

      {/* Invoice sent */}
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box>
          <Typography whiteSpace="nowrap" variant="body1">
            {data.whencreated
              ? new Date(data.whencreated).toLocaleDateString('en-us')
              : '—'}
          </Typography>
        </Box>
      </CustomTableCell>

      {/* Due date */}
      <CustomTableCell
        sx={{
          borderBottom: '0',
          minWidth: columnWidth,
          py: 1,
          color: isDueDateInPast ? 'red' : 'black',
        }}
        pure
        nowrap
      >
        <Box>
          <Typography whiteSpace="nowrap" variant="body1">
            {new Date(data.invoiceDueDate).toLocaleDateString('en-us')}
          </Typography>
          {isDueDateInPast && (
            <Typography whiteSpace="nowrap" variant="body1">
              {DateTime.now()
                .diff(DateTime.fromFormat(data.invoiceDueDate, 'MM/dd/yyyy'), [
                  'days',
                ])
                .toFormat('dd')}{' '}
              days ago
            </Typography>
          )}
        </Box>
      </CustomTableCell>

      {/* Paid on */}
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box>
          <Typography
            whiteSpace="nowrap"
            variant="body2"
            color={data.isPaidAfterPastDue ? 'red' : 'black'}
          >
            {data.paidOn
              ? new Date(data.paidOn).toLocaleDateString('en-us')
              : '—'}
          </Typography>
        </Box>
      </CustomTableCell>

      {/* Paid after past due */}
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box>
          <Typography
            whiteSpace="nowrap"
            variant="body1"
            sx={{ color: numberOfDueDays === '—' ? '' : 'red' }}
          >
            {numberOfDueDays}
          </Typography>
        </Box>
      </CustomTableCell>
    </StyledTableRow>
  );
};
